import { useEffect, type ComponentType, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Inbox, Menu, MessagesSquare, UsersRound } from 'lucide-react';
import { useSidebar } from '@/components/ui/sidebar';
import { useMentionCount } from '@/components/mentions/useMentionCount';
import {
	useDrawerAttention,
	useInboxUnreadCount,
	useReminderCounts,
	useTeamUnreadCount,
} from '@/components/sidebar/use-nav-badges';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useHideMobileTabBar, useMobileChrome } from './MobileChrome';
import { useKeyboardOpen } from './keyboard';

const INBOX_LIST_PATH = /^\/(inbox|unassigned|my-messages|folders|tags|locations|users|departments)(\/|$)/;
const TEAM_LIST_PATH = /^\/groupchat\/(groups|dms)\/?$/;
const LAST_INBOX_KEY = 'mobile:lastInboxPath';
const LAST_TEAM_KEY = 'mobile:lastTeamPath';

// Full-screen flows and pushed screens (a conversation, an editor) have no
// tab bar, like a native app. Screens can also hide it with useHideMobileTabBar.
// /onboarding keeps it: only accounts exploring the dashboard get the bar
// there, and it is their way back to the inbox.
const NO_TAB_BAR_PATH = new RegExp(
	[
		'^/ai/start(/|$)',
		'/conversation/[^/]+',
		'^/inbox/new/?$',
		'^/customers/[^/]+',
		'^/work/(create|edit|view)(/|$)',
		'^/pipeline/item/',
		'^/payments/details/',
	].join('|'),
);

/** The list the Inbox tab returns to: the current inbox view, without an open conversation. */
const inboxListPath = (pathname: string) => {
	const list = pathname.replace(/\/conversation\/.*$/, '').replace(/\/new\/?$/, '');
	return list || '/inbox';
};

const readPath = (key: string, fallback: string) => {
	try {
		return sessionStorage.getItem(key) || fallback;
	} catch {
		return fallback;
	}
};

const writePath = (key: string, path: string) => {
	try {
		sessionStorage.setItem(key, path);
	} catch {
		/* storage may be disabled */
	}
};

/** Scrolls whatever the current screen scrolls back to the top, like re-tapping an iOS tab. */
const scrollPageToTop = () => {
	const page = document.querySelector<HTMLElement>('[data-app-page]');
	if (!page) return;
	for (const el of [page, ...page.querySelectorAll<HTMLElement>('*')]) {
		if (el.scrollTop > 0 && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
			el.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}
};

interface Tab {
	key: string;
	label: string;
	icon: ComponentType<{ className?: string; strokeWidth?: number }>;
	/** Where tapping the tab goes (the tab's last list). */
	to: string;
	/** Where re-tapping the active tab goes (its root list). */
	root: string;
	badge?: number;
	/** What the badge counts, for VoiceOver. */
	badgeLabel?: string;
	badgeClassName?: string;
	match: (pathname: string) => boolean;
}

const formatBadge = (count: number) => (count > 99 ? '99+' : String(count));

/**
 * The count on a tab's icon, or on a shortcut tile's (Work's Today). Hidden
 * from VoiceOver: put the count in the control's text (`sr-only`) as well.
 * Defaults to red; pass a background (bg-blue-500) for counts that aren't urgent.
 */
export function IconBadge({ count, className }: { count: number; className?: string }) {
	return (
		<span
			aria-hidden
			className={cn(
				'absolute -right-2.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold leading-none text-white ring-2 ring-background',
				className,
			)}
		>
			{formatBadge(count)}
		</span>
	);
}

/**
 * Bottom tab bar for phones. Holds the screens people use most from a phone;
 * everything else lives in the navigation drawer behind "More". Not mounted
 * at all from the md breakpoint, so desktop runs none of its badge polling.
 * Today's event count shows on the drawer's Calendar row but doesn't light
 * the More dot: it's information, not something to act on.
 */
export function MobileTabBar() {
	const isMobile = useIsMobile();
	return isMobile ? <PhoneTabBar /> : null;
}

function PhoneTabBar() {
	const { pathname, search } = useLocation();
	const navigate = useNavigate();
	const { setOpenMobile, openMobile } = useSidebar();
	const { tabBarHidden } = useMobileChrome();
	const keyboardOpen = useKeyboardOpen();
	const inboxUnread = useInboxUnreadCount();
	const teamUnread = useTeamUnreadCount();
	const reminders = useReminderCounts();
	const mentionCount = useMentionCount();
	const teamMentionCount = useMentionCount(true);
	const drawerAttention = useDrawerAttention();

	// Also drops --mobile-tabbar-height so fixed UI follows the bar down.
	useHideMobileTabBar(NO_TAB_BAR_PATH.test(pathname));

	useEffect(() => {
		if (INBOX_LIST_PATH.test(pathname) && !pathname.includes('/conversation/')) {
			writePath(LAST_INBOX_KEY, inboxListPath(pathname) + search);
		}
		if (TEAM_LIST_PATH.test(pathname)) writePath(LAST_TEAM_KEY, pathname.replace(/\/$/, ''));
	}, [pathname, search]);

	const inboxRoot = INBOX_LIST_PATH.test(pathname) ? inboxListPath(pathname) : readPath(LAST_INBOX_KEY, '/inbox');
	const teamRoot = TEAM_LIST_PATH.test(pathname) ? pathname.replace(/\/$/, '') : readPath(LAST_TEAM_KEY, '/groupchat/groups');

	const tabs: Tab[] = [
		{
			key: 'inbox',
			label: 'Inbox',
			icon: Inbox,
			to: inboxRoot,
			root: inboxRoot,
			badge: inboxUnread,
			badgeLabel: 'unread',
			match: (p) =>
				p === '/' ||
				INBOX_LIST_PATH.test(p) ||
				p.startsWith('/mentions') ||
				(p.startsWith('/conversation/') && !p.startsWith('/conversation/groups')),
		},
		{
			key: 'team',
			label: 'Team',
			icon: MessagesSquare,
			to: teamRoot,
			root: teamRoot,
			badge: teamUnread,
			badgeLabel: 'unread',
			match: (p) => p.startsWith('/groupchat') || p.startsWith('/team-mentions') || p.startsWith('/conversation/groups'),
		},
		{
			key: 'contacts',
			label: 'Contacts',
			icon: UsersRound,
			to: '/customers',
			root: '/customers',
			match: (p) => p.startsWith('/customers') || p.startsWith('/contacts'),
		},
		{
			key: 'reminders',
			label: 'Reminders',
			icon: Bell,
			to: '/reminders',
			root: '/reminders',
			badge: reminders.total,
			badgeLabel: reminders.overdue > 0 ? 'overdue or due today' : 'due today',
			// Red when something is overdue, blue when it is only due today (as in the sidebar).
			badgeClassName: reminders.overdue > 0 ? 'bg-red-500' : 'bg-blue-500',
			match: (p) => p.startsWith('/reminders'),
		},
	];

	const activeKey = tabs.find((tab) => tab.match(pathname))?.key ?? 'more';
	const moreDot = mentionCount > 0 || teamMentionCount > 0 || drawerAttention;

	if (tabBarHidden || keyboardOpen) return null;

	const onTabClick = (event: MouseEvent, tab: Tab) => {
		if (activeKey !== tab.key) return;
		// Re-tapping the current tab pops it back to its list, or scrolls the list to the top.
		event.preventDefault();
		if (pathname + search !== tab.root && pathname !== tab.root) navigate(tab.root);
		else {
			scrollPageToTop();
			window.dispatchEvent(new CustomEvent('mobile-tab-reselect', { detail: { tab: tab.key } }));
		}
	};

	const itemClass = (active: boolean) =>
		cn(
			'relative flex h-[3.25rem] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 pt-0.5 text-[10.5px] font-medium tracking-tight transition-colors active:opacity-60',
			'outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring [-webkit-touch-callout:none]',
			active ? 'text-primary dark:text-foreground' : 'text-muted-foreground',
		);

	return (
		<nav
			aria-label="Tab bar"
			data-mobile-tabbar
			className="z-40 shrink-0 select-none border-t border-border/70 bg-background/90 pb-safe pl-safe pr-safe backdrop-blur-xl supports-[backdrop-filter]:bg-background/80 md:hidden"
		>
			<div className="flex px-1">
				{tabs.map((tab) => {
					const { key, label, icon: Icon, to, badge, badgeLabel, badgeClassName } = tab;
					const active = activeKey === key;
					return (
						<Link
							key={key}
							to={to}
							onClick={(event) => onTabClick(event, tab)}
							aria-current={active ? 'page' : undefined}
							className={itemClass(active)}
							data-mobile-tab={key}
							data-onboarding={key === 'inbox' ? 'inbox' : key === 'contacts' ? 'contacts' : key === 'team' ? 'teams-groups' : undefined}
						>
							<span className="relative">
								<Icon className="size-6" strokeWidth={active ? 2.2 : 1.75} />
								{!!badge && <IconBadge count={badge} className={badgeClassName} />}
							</span>
							<span className="max-w-full truncate">{label}</span>
							{!!badge && <span className="sr-only">{`, ${badge} ${badgeLabel ?? ''}`}</span>}
						</Link>
					);
				})}
				<button
					type="button"
					onClick={() => setOpenMobile(!openMobile)}
					aria-haspopup="dialog"
					aria-expanded={openMobile}
					aria-current={activeKey === 'more' ? 'page' : undefined}
					className={itemClass(activeKey === 'more')}
					data-mobile-tab="more"
					data-onboarding="mobile-navigation"
				>
					<span className="relative">
						<Menu className="size-6" strokeWidth={activeKey === 'more' ? 2.2 : 1.75} />
						{moreDot && (
							<span aria-hidden className="absolute -right-1 -top-0.5 size-2.5 rounded-full bg-red-500 ring-2 ring-background" />
						)}
					</span>
					<span>More</span>
					{moreDot && <span className="sr-only">, needs attention</span>}
				</button>
			</div>
		</nav>
	);
}
